import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useToast } from '@raptormaps/toast';
import { TextInput } from '@raptormaps/text-input';
import { Button } from '@raptormaps/button';
import { Text } from '@raptormaps/text';
import { getUserSearchQuery } from '../../api/services/users';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  UserRow,
  RowState,
  OffboardResult,
} from '../../components/UserManagement/UserRow';
import '../../components/UserManagement/UserManagement.css';

const MIN_QUERY_LENGTH = 2;

export function UserManagement() {
  const toast = useToast();

  const [searchInput, setSearchInput] = useState('');
  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});
  const [rowChecked, setRowChecked] = useState<Record<number, boolean>>({});
  const [offboardResults, setOffboardResults] = useState<
    Record<number, OffboardResult>
  >({});

  const debouncedSearch = useDebouncedValue(searchInput.trim(), 300);
  const isActive = debouncedSearch.length >= MIN_QUERY_LENGTH;

  const { data: searchResults, isFetching } = useQuery({
    ...getUserSearchQuery(debouncedSearch),
    enabled: isActive,
    onError: () => {
      toast.error('Failed to search users. Please try again.');
    },
  });

  useEffect(() => {
    setRowStates({});
    setRowChecked({});
    setOffboardResults({});
  }, [debouncedSearch]);

  const handleClear = useCallback(() => {
    setSearchInput('');
  }, []);

  const setRowState = useCallback((userId: number, state: RowState) => {
    setRowStates(prev => ({ ...prev, [userId]: state }));
  }, []);

  const setChecked = useCallback((userId: number, checked: boolean) => {
    setRowChecked(prev => ({ ...prev, [userId]: checked }));
  }, []);

  return (
    <div className="user-management">
      <div className="user-management__search-row">
        <div className="user-management__search-input">
          <div className="user-management__input-wrap">
            <TextInput
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="tertiary"
                className="user-management__input-clear"
                onClick={handleClear}
                aria-label="Clear search"
              >
                ✕
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="user-management__results">
        {!searchInput.trim() ? (
          <div className="user-management__empty">
            <Text variant="paragraph_medium" color="neutral_600">
              Enter a name or email to search.
            </Text>
          </div>
        ) : !isActive ? (
          <div className="user-management__empty">
            <Text variant="paragraph_medium" color="neutral_600">
              Keep typing…
            </Text>
          </div>
        ) : isFetching ? (
          <div className="user-management__loading">
            <Text variant="paragraph_medium" color="neutral_600">
              Searching...
            </Text>
          </div>
        ) : !searchResults || searchResults.users.length === 0 ? (
          <div className="user-management__empty">
            <Text variant="paragraph_medium" color="neutral_600">
              No users found matching &ldquo;{debouncedSearch}&rdquo;.
            </Text>
          </div>
        ) : (
          <div className="user-management__list">
            {searchResults.users.map(user => (
              <UserRow
                key={user.id}
                user={user}
                rowState={rowStates[user.id] ?? 'idle'}
                checked={rowChecked[user.id] ?? false}
                offboardResult={offboardResults[user.id] ?? null}
                onSetState={state => setRowState(user.id, state)}
                onSetChecked={checked => setChecked(user.id, checked)}
                onOffboardResult={result =>
                  setOffboardResults(prev => ({ ...prev, [user.id]: result }))
                }
                onError={() => {
                  toast.error('Failed to offboard user. Please try again.');
                  setRowState(user.id, 'confirming');
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
